import { auth } from './auth.js';

/**
 * EventSource manager.
 *
 * - Auto-reconnect on error with exponential backoff (capped).
 * - Multiple named event handlers (snapshot, vitals, counts, storage).
 * - Heartbeat tracking via "ka" comments isn't directly observable in JS
 *   (browser doesn't fire onmessage for `:` comments), but `onerror` fires
 *   if connection drops — which is what we react to.
 * - Auth is passed via ?token=<jwt> query because the EventSource API
 *   cannot set custom request headers. The token is fetched fresh from
 *   the auth module on every (re)connect.
 */
export class Stream {
  constructor() {
    this.es = null;
    this.url = null;
    this.handlers = new Map();
    this.openHandlers = new Set();
    this.closeHandlers = new Set();
    this.reconnectTimer = null;
    this.reconnectDelay = 1000;
    this.maxReconnectDelay = 15000;
    // Robustness (P1, Hardening-Plan 2026-06-14): Reconnect-Storms entschaerfen.
    this.failures = 0;             // aufeinanderfolgende Fehlschlaege (reset bei onopen)
    this._opened = false;          // ist diese Verbindung je aufgegangen?
    this.breakerThreshold = 8;     // ab hier Circuit-Breaker: langes Cool-down
    this.breakerDelay = 60000;     // statt jeden Worker im Sekundentakt zu grabben
    this._lastRefreshAt = 0;       // Refresh-Throttle (kein refresh pro Reconnect)
    this.refreshCooldownMs = 30000;
  }

  on(eventType, fn) {
    this.handlers.set(eventType, fn);
    if (this.es) {
      this.es.addEventListener(eventType, e => this._dispatch(eventType, e));
    }
    return this;
  }

  onOpen(fn) { this.openHandlers.add(fn); return this; }
  onClose(fn) { this.closeHandlers.add(fn); return this; }

  _dispatch(eventType, e) {
    const fn = this.handlers.get(eventType);
    if (!fn) return;
    try {
      fn(JSON.parse(e.data));
    } catch (err) {
      console.error(`[stream] handler ${eventType} failed:`, err);
    }
  }

  _withToken(url) {
    const token = auth.getToken();
    if (!token) return url;
    const sep = url.includes('?') ? '&' : '?';
    return url + sep + 'token=' + encodeURIComponent(token);
  }

  connect(url) {
    this.disconnect();
    this.url = url;
    const target = this._withToken(url);
    this._opened = false;
    this.es = new EventSource(target, { withCredentials: true });

    this.es.onopen = () => {
      this.reconnectDelay = 1000;
      this.failures = 0;
      this._opened = true;
      for (const fn of this.openHandlers) fn();
    };

    for (const [type] of this.handlers) {
      this.es.addEventListener(type, e => this._dispatch(type, e));
    }

    this.es.onerror = () => {
      this._reconnect();
    };
  }

  async _reconnect() {
    // War die Verbindung vor dem Fehler je offen? (network-drop vs. sofortiger
    // Reject) — VOR disconnect() lesen, das setzt nichts zurueck, aber connect()
    // wuerde _opened neu auf false setzen.
    const wasOpen = this._opened;
    this.disconnect();
    for (const fn of this.closeHandlers) fn();
    if (!this.url) return;
    this.failures++;

    // Circuit-Breaker: nach breakerThreshold Fehlschlaegen auf ein langes
    // Cool-down-Ceiling wechseln, statt im Sekundentakt Worker zu grabben.
    // (Kein harter Stop — so erholt sich der Client, wenn der Server zurueckkommt.)
    const ceiling = this.failures >= this.breakerThreshold
      ? this.breakerDelay : this.maxReconnectDelay;
    const base = Math.min(this.reconnectDelay, ceiling);
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, ceiling);
    // Equal-Jitter (AWS-Pattern "Exponential Backoff And Jitter"): delay in
    // [base/2, base] — bricht den Lockstep besser als zentrierter Jitter
    // (geringste Contention bei vielen gleichzeitig reconnectenden Tabs),
    // bleibt aber >= base/2 (kein zu-aggressives Retry).
    const delay = Math.max(500, Math.round(base * (0.5 + Math.random() * 0.5)));

    // Auth-Refresh NICHT vor jedem Reconnect (verdoppelt den Druck). Nur wenn
    // ein Auth-Fehler plausibel ist (Verbindung ging NIE auf → wahrscheinlich
    // 401/403; ein Drop einer offenen Verbindung ist ein Netzproblem, kein
    // Token-Problem) — und hoechstens einmal pro refreshCooldownMs.
    const maybeAuth = !wasOpen;
    this.reconnectTimer = setTimeout(async () => {
      if (maybeAuth && (Date.now() - this._lastRefreshAt) > this.refreshCooldownMs) {
        this._lastRefreshAt = Date.now();
        try { await auth.refresh(); } catch { /* reconnect versucht's trotzdem */ }
      }
      this.connect(this.url);
    }, delay);
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.es) {
      this.es.close();
      this.es = null;
    }
  }
}
